import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, FileText, Instagram, Play, X } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { RevealLine, ScrollReveal } from "@/components/Reveal";
import { HOW_TO_VIDEO, EDUCATION_SECTIONS, eduThumb, eduPlayerUrl, eduViewUrl } from "@/lib/education";

const HERO_IMG = "https://lh3.googleusercontent.com/d/1YXemrchNT0MQje47Rk1isdGYi6W7BJtT=w2000";

const VideoModal = ({ video, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!video) return null;
  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-foreground/80 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="video-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-4xl border border-border bg-background"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <p className="truncate font-serif text-lg font-semibold" data-testid="video-modal-title">{video.name}</p>
          <button onClick={onClose} data-testid="video-modal-close" className="text-muted-foreground transition-colors hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="aspect-video w-full bg-black">
          <iframe
            src={eduPlayerUrl(video.id)}
            title={video.name}
            className="h-full w-full"
            allow="autoplay; fullscreen"
            allowFullScreen
          />
        </div>
      </motion.div>
    </div>
  );
};

const VideoCard = ({ item, onPlay, index }) => (
  <ScrollReveal delay={(index % 3) * 0.1}>
    {item.type === "video" ? (
      <button
        onClick={() => onPlay(item)}
        data-testid={`edu-video-${item.id.slice(0, 8)}`}
        className="group block w-full border border-border bg-card text-left transition-colors duration-500 hover:border-foreground/40"
      >
        <div className="relative aspect-video overflow-hidden bg-foreground/5">
          <img
            src={eduThumb(item.id)}
            alt={item.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
          />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background/90 shadow-lg backdrop-blur-md transition-transform duration-300 group-hover:scale-110">
              <Play className="ml-0.5 h-5 w-5 text-primary" />
            </span>
          </span>
        </div>
        <div className="p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">{item.tag}</p>
          <h3 className="mt-2 font-serif text-xl font-semibold leading-snug tracking-tight">{item.name}</h3>
        </div>
      </button>
    ) : (
      <a
        href={eduViewUrl(item.id)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`edu-pdf-${item.id.slice(0, 8)}`}
        className="group flex h-full flex-col border border-dashed border-border bg-card transition-colors duration-500 hover:border-primary"
      >
        <div className="flex aspect-video items-center justify-center bg-secondary/50">
          <FileText className="h-10 w-10 text-primary transition-transform duration-300 group-hover:-translate-y-1" />
        </div>
        <div className="p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">{item.tag} · PDF guide</p>
          <h3 className="mt-2 font-serif text-xl font-semibold leading-snug tracking-tight">{item.name}</h3>
        </div>
      </a>
    )}
  </ScrollReveal>
);

export default function Education() {
  const [playing, setPlaying] = useState(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 180]);

  return (
    <div className="min-h-screen" data-testid="education-page">
      <Navbar />

      {/* Hero */}
      <section className="relative flex h-[55vh] items-center justify-center overflow-hidden" data-testid="education-hero">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <img
            src={HERO_IMG}
            srcSet="https://lh3.googleusercontent.com/d/1YXemrchNT0MQje47Rk1isdGYi6W7BJtT=w900 900w, https://lh3.googleusercontent.com/d/1YXemrchNT0MQje47Rk1isdGYi6W7BJtT=w1400 1400w, https://lh3.googleusercontent.com/d/1YXemrchNT0MQje47Rk1isdGYi6W7BJtT=w2000 2000w"
            sizes="100vw"
            alt="Barn swallows perched together on campus"
            className="h-[125%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-black/35" />
        </motion.div>
        <div className="relative text-center">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-xs font-bold uppercase tracking-[0.35em] text-[#f6f3ea]/80"
          >
            CK Finding Fauna · Learn
          </motion.p>
          <h1
            className="mt-4 font-serif text-[16vw] font-semibold leading-none tracking-tighter text-[#f6f3ea] md:text-[9vw]"
            data-testid="education-title"
          >
            <RevealLine delay={0.2}>Education<span className="text-primary">.</span></RevealLine>
          </h1>
        </div>
      </section>

      {/* How to navigate */}
      <section className="mx-auto max-w-4xl px-6 py-20 md:py-28" data-testid="howto-section">
        <ScrollReveal>
          <p className="text-xs font-bold uppercase tracking-[0.35em] text-primary">Start here</p>
          <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight md:text-4xl">{HOW_TO_VIDEO.name}</h2>
          <p className="mt-3 text-sm text-muted-foreground">{HOW_TO_VIDEO.note}</p>
        </ScrollReveal>
        <ScrollReveal delay={0.12}>
          <div className="mt-8 aspect-video w-full border border-border bg-black" data-testid="howto-player">
            <iframe
              src={eduPlayerUrl(HOW_TO_VIDEO.id)}
              title={HOW_TO_VIDEO.name}
              className="h-full w-full"
              allow="autoplay; fullscreen"
              allowFullScreen
            />
          </div>
        </ScrollReveal>
      </section>

      {/* Video library */}
      {EDUCATION_SECTIONS.map((section) => (
        <section key={section.key} className="mx-auto max-w-7xl px-6 pb-20 md:px-16 md:pb-24" data-testid={`edu-section-${section.key}`}>
          <ScrollReveal className="mb-10 flex flex-wrap items-end justify-between gap-4 border-t border-border pt-12">
            <div>
              <h2 className="font-serif text-3xl font-semibold tracking-tight md:text-5xl">{section.title}</h2>
              <p className="mt-3 text-sm text-muted-foreground">{section.blurb}</p>
            </div>
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
              {section.items.length} {section.items.length === 1 ? "item" : "items"}
            </p>
          </ScrollReveal>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {section.items.map((item, i) => (
              <VideoCard key={item.id} item={item} onPlay={setPlaying} index={i} />
            ))}
          </div>
          <ScrollReveal>
            <p className="mt-10 font-serif text-lg italic text-muted-foreground">
              More episodes are in production — amphibians, birds, fish, mammals and reptiles are coming soon.
            </p>
          </ScrollReveal>
        </section>
      ))}

      {/* Drive folder link */}
      <section className="mx-auto max-w-7xl px-6 pb-24 md:px-16" data-testid="edu-drive-section">
        <ScrollReveal>
          <a
            href="https://drive.google.com/drive/folders/1-Tlg2POUtvTxqdwj9N9mChfjDcWXSsee"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="edu-drive-link"
            className="group flex flex-col items-start justify-between gap-6 border-2 border-foreground bg-card p-8 transition-colors duration-300 hover:bg-foreground hover:text-background md:flex-row md:items-center md:p-10"
          >
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">Always up to date</p>
              <h2 className="mt-3 font-serif text-2xl font-semibold tracking-tight md:text-3xl">
                New videos are added to our Google Drive library first
              </h2>
              <p className="mt-2 text-sm text-muted-foreground transition-colors duration-300 group-hover:text-background/70">
                Browse every episode — organized by audience and animal group.
              </p>
            </div>
            <span className="flex shrink-0 items-center gap-2 border border-foreground px-6 py-4 text-xs font-bold uppercase tracking-[0.25em] transition-colors duration-300 group-hover:border-background">
              Open the video library
              <ArrowUpRight className="h-4 w-4 text-primary transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </span>
          </a>
        </ScrollReveal>
      </section>

      {/* Links strip */}
      <footer className="border-t border-border bg-foreground text-background" data-testid="education-footer">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-6 py-10 md:px-16">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-background/50">Links</p>
          <a
            href="https://instagram.com/ck_findingfauna"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="education-instagram-link"
            className="flex items-center gap-2 text-sm transition-colors duration-300 hover:text-primary"
          >
            <Instagram className="h-4 w-4" />
            @ck_findingfauna
          </a>
        </div>
      </footer>

      <VideoModal video={playing} onClose={() => setPlaying(null)} />
    </div>
  );
}
